<template>
  <vd-error
    v-if="!loading && errorState.status"
    :status="errorState.status"
    :message="errorState.message"
    :back-link="backLink"
  />
  <div v-if="loading" v-loading></div>
  <VdPage
    v-else
    class="abtest-stepper-component"
    v-bind="$attrs"
    :title="pageTitle"
    show-back-arrow
    with-background
    :loading="loading"
    @back="onBack"
  >
    <template #subtitle>
      {{ pageSubtitle }}
      <VdTooltip v-if="isPendingOrPublishedAbtest" class="status-icon" orientation="top" :content="titleTooltipContent">
        <vd-custom-icon :name="`${abtestState}-icon`" />
      </VdTooltip>
    </template>

    <template #actions>
      <el-steps :active="currentStepIndex" align-center finish-status="success">
        <el-step
          v-for="(step, index) in steps"
          :key="index"
          :title="step.name"
          :class="{ clickable: isStepClickable(index) }"
          @click="() => onStepClicked(index)"
        ></el-step>
      </el-steps>
    </template>

    <slot />
  </VdPage>
</template>

<script lang="ts">
import { VdPage, VdTooltip } from "@voodoo.io/voodoo-ui";
import { computed, defineComponent, PropType } from "vue";
import { useRoute, useRouter } from "vue-router";

import { AbtestState } from "@/graphql";
import links from "@/router/links";

interface Abtest {
  name: string;
  state: AbtestState;
  isDynamic: boolean;
}

export default defineComponent({
  name: "AbtestStepper",
  components: {
    VdTooltip,
    VdPage,
  },
  props: {
    gameId: {
      type: String,
      required: true,
    },
    versionId: {
      type: String,
      required: true,
    },
    abtestId: {
      type: String,
      required: false,
      default: null,
    },
    abtest: {
      type: Object as PropType<Abtest>,
      required: false,
      default: null,
    },
    inWipVersion: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },

  setup(props) {
    const router = useRouter();
    const route = useRoute();

    const steps = computed(() => [
      { name: "Infos & Audiences", route: links.games.abtests.edit },
      ...(props.abtest?.isDynamic ? [{ name: "Dynamic Treatment", route: links.games.abtests.dynamicTreatment }] : []),
      { name: "Cohorts", route: links.games.abtests.cohorts.list },
      { name: "Review & Validation", route: links.games.abtests.review },
    ]);

    /**
     * Handle errors
     */
    const errorState = computed<{ status: string; message: string }>(() => {
      if (props.abtestId && !props.abtest) {
        return { status: "404", message: "The A/B Test you are looking for does not exist" };
      }

      if (props.abtest && !props.inWipVersion) {
        return { status: "405", message: "You can not edit the old version of this A/B Test." };
      }

      return { status: "", message: "" };
    });

    const isPendingOrPublishedAbtest = computed(
      () => props.abtest && [AbtestState.Pending, AbtestState.Published].includes(props.abtest.state)
    );
    const abtestState = computed(() => props.abtest?.state ?? AbtestState.Draft);

    const pageTitle = computed(() => {
      // Change the title if the user is creating an IAP A/B Test
      if (route.query.iap && route.name === links.games.abtests.new) {
        return "New IAP A/B Test";
      }

      return props.abtest?.name ?? "New A/B Test";
    });

    const pageSubtitle = computed(() => (abtestState.value === AbtestState.Draft ? "draft" : ""));

    const titleTooltipContent = computed(() =>
      abtestState.value === AbtestState.Published
        ? "This A/B Test is currently running"
        : "This A/B Test is waiting for publication"
    );

    /**
     * Routing
     */
    const stepRoutes = computed(() => [
      links.games.abtests.edit,
      ...(props.abtest?.isDynamic ? [links.games.abtests.dynamicTreatment] : []),
      links.games.abtests.cohorts.list,
      links.games.abtests.review,
    ]);

    const currentStepIndex = computed(() => {
      const routeName = route.name as string;

      if (!routeName) {
        return 0;
      }
      if (routeName === links.games.abtests.new) {
        return 0;
      }
      return stepRoutes.value.indexOf(routeName);
    });

    const isStepClickable = (stepIndex: number) => {
      if (isPendingOrPublishedAbtest.value) {
        return false;
      }
      return stepIndex !== currentStepIndex.value && (stepIndex < currentStepIndex.value || !!props.abtest);
    };

    const onStepClicked = (stepIndex: number) => {
      if (!props.abtestId) {
        return;
      }

      if (isStepClickable(stepIndex)) {
        router.push({
          name: stepRoutes.value[stepIndex],
          params: {
            gameId: props.gameId,
            versionId: props.versionId,
            abtestId: props.abtestId,
          },
        });
      }
    };

    const backLink = computed(() => {
      const params = { gameId: props.gameId, versionId: props.versionId };
      if (!isPendingOrPublishedAbtest.value && currentStepIndex.value > 0) {
        return { name: stepRoutes.value[currentStepIndex.value - 1], params };
      }
      return { name: links.games.abtests.list, params };
    });

    const onBack = () => {
      router.push(backLink.value);
    };

    return {
      errorState,
      currentStepIndex,
      isStepClickable,
      onStepClicked,
      backLink,
      onBack,
      pageTitle,
      pageSubtitle,
      titleTooltipContent,
      isPendingOrPublishedAbtest,
      abtestState,
      steps,
    };
  },
});
</script>

<style lang="scss">
.abtest-stepper-component {
  .el-steps {
    width: 400px;

    i.el-icon svg {
      color: white;
    }
  }

  .el-step__title {
    line-height: 20px;
    margin-top: 7px;

    &.is-process {
      color: var(--vd-base-primary-color);
    }
  }

  .clickable {
    cursor: pointer;
  }

  .status-icon {
    margin-left: size("S");
    align-self: flex-end;
  }

  &.vd-page-component .head .actions {
    width: 50%;
    justify-content: flex-end;
  }
}
</style>
